// Adapter for GET /v1/comments/by_date. The v1 jbuilder groups by display-
// date strings ("Monday May 18 '26"), with an optional top-level `pinned`
// array. The customer-portal doesn't need the date labels — every comment
// carries `created_at` in ISO 8601 so the UI groups client-side — so we
// flatten the response to a single sorted-desc array and surface `pinned`
// as a per-comment flag.

export interface CommentUserWire {
  type: string;
  id: number;
  email: string;
  full_name: string;
}

export interface CommentWire {
  id: number;
  comment: string;
  commentable_type: string;
  commentable_id: number;
  pinned: boolean;
  customer_visible: boolean;
  edited: boolean;
  editable: boolean;
  commentable_editable: boolean;
  customer_visible_editable: boolean;
  created_at: string;
  updated_at: string;
  user?: CommentUserWire;
  message_key?: string;
}

// Top-level v1 response — `pinned` is the only known string key; everything
// else is a display-date label whose contents are an array of CommentWire.
export type CommentsByDateResponse = Record<string, CommentWire[]>;

export interface AppointmentComment {
  id: number;
  comment: string;
  pinned: boolean;
  customerVisible: boolean;
  edited: boolean;
  createdAt: string;
  authorName: string;
  authorType: string;
  isCustomerAuthored: boolean;
}

function adaptOne(wire: CommentWire): AppointmentComment {
  const authorType = wire.user?.type ?? '';
  return {
    id: wire.id,
    comment: wire.comment,
    pinned: Boolean(wire.pinned),
    customerVisible: Boolean(wire.customer_visible),
    edited: Boolean(wire.edited),
    createdAt: wire.created_at,
    authorName: wire.user?.full_name ?? '',
    authorType,
    isCustomerAuthored: authorType === 'Customer',
  };
}

export function adaptCommentsByDateResponse(
  response: CommentsByDateResponse | null | undefined,
): AppointmentComment[] {
  if (!response) return [];
  const comments: AppointmentComment[] = [];
  for (const value of Object.values(response)) {
    if (!Array.isArray(value)) continue;
    for (const c of value) {
      if (c && typeof c.id === 'number') comments.push(adaptOne(c));
    }
  }
  // Pinned first, then desc by created_at. ISO 8601 strings sort
  // lexicographically — no Date allocation required.
  comments.sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return b.createdAt.localeCompare(a.createdAt);
  });
  return comments;
}
